import { httpsCallable } from 'firebase/functions';
import { CloudFunctions } from '../utils/firebase/firebase';

// Replace the throttling variables with simple caching
const teacherLoginCache = new Map();
const pendingTeacherRequests = new Map(); // ADD THIS: Track pending requests
const studentLoginCache = new Map();
const pendingStudentRequests = new Map(); // Track pending student requests

export const clearStudentLoginCache = (uid = null) => {
  if (uid) {
    studentLoginCache.delete(uid);
    pendingStudentRequests.delete(uid);
  } else {
    studentLoginCache.clear();
    pendingStudentRequests.clear();
  }
};

export const clearTeacherLoginCache = (uid = null) => {
  if (uid) {
    teacherLoginCache.delete(uid);
    pendingTeacherRequests.delete(uid);
  } else {
    teacherLoginCache.clear();
    pendingTeacherRequests.clear();
  }
};

// Expose cache clearing functions globally for use in other modules
if (typeof window !== 'undefined') {
  window.clearStudentLoginCache = clearStudentLoginCache;
  window.clearTeacherLoginCache = clearTeacherLoginCache;
}

export const validateAuthCodeAndGetDoctor = async (authCode, uid) => {
  try {
    const validateAuthCode = httpsCallable(CloudFunctions, 'validateAuthCode');

    const response = await validateAuthCode({
      authCode,
      uid,
    });

    if (response.data.success) {
      return response.data.data;
    } else {
      return response;
    }
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const patientLogin = async (uid) => {
  try {
    const validatePatientLogin = httpsCallable(
      CloudFunctions,
      'validatePatientLogin'
    );

    const response = await validatePatientLogin({
      uid,
    });
    if (response?.data?.status) {
      return response?.data?.data;
    } else {
      return null;
    }
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const doctorLogin = async (uid) => {
  try {
    const validateDoctorLogin = httpsCallable(
      CloudFunctions,
      'validateDoctorLogin'
    );

    const response = await validateDoctorLogin({
      uid,
    });
    if (response.data.status) {
      return response.data.data;
    } else {
      return null;
    }
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const adminLogin = async (uid) => {
  try {
    const validateAdminLogin = httpsCallable(
      CloudFunctions,
      'validateAdminLogin'
    );
    const response = await validateAdminLogin({ uid });
    if (response.data.status) {
      return response.data.data;
    } else {
      console.warn('Admin validation failed:', response.data.message);
      return null;
    }
  } catch (error) {
    console.error('Error during admin login:', error);
    throw error;
  }
};

export const studentLogin = async (uid) => {
  try {
    // SIMPLE CACHE: Return cached result if available
    if (studentLoginCache.has(uid)) {
      return studentLoginCache.get(uid);
    }

    // CHECK 2: If request already in progress, return the existing promise
    if (pendingStudentRequests.has(uid)) {
      return pendingStudentRequests.get(uid);
    }

    // CHECK 3: Create new request and store it
    const requestPromise = performStudentLogin(uid);
    pendingStudentRequests.set(uid, requestPromise);

    try {
      const result = await requestPromise;

      // Cache successful result
      if (result) {
        studentLoginCache.set(uid, result);
      }

      // Clean up pending request
      pendingStudentRequests.delete(uid);
      return result;
    } catch (error) {
      // Clean up pending request on error
      pendingStudentRequests.delete(uid);
      throw error;
    }
  } catch (error) {
    console.error('Error during student login:', error);
    throw error;
  }
};

const performStudentLogin = async (uid) => {
  const getStudentProfile = httpsCallable(CloudFunctions, 'getStudentProfile');
  const response = await getStudentProfile({ uid });
  if (response.data.status) {
    // Normalize typo from backend: myDeatils -> myDetails
    const data = response.data.data || {};
    if (data && data.myDeatils && !data.myDetails) {
      data.myDetails = data.myDeatils;
    }
    // Apply client-side overrides (immediate consistency after profile update)
    try {
      const overridesRaw =
        typeof window !== 'undefined'
          ? localStorage.getItem(`studentProfileOverrides:${uid}`)
          : null;
      if (overridesRaw) {
        const overrides = JSON.parse(overridesRaw);
        const serverTs = (() => {
          const ts = data?.myDetails?.timestamp;
          if (!ts) return 0;
          if (typeof ts === 'string' || typeof ts === 'number') {
            return new Date(ts).getTime() || 0;
          }
          if (ts?._seconds) {
            return ts._seconds * 1000;
          }
          return 0;
        })();
        const overrideTs = overrides?.updatedAt || 0;
        if (overrideTs && overrideTs > serverTs) {
          data.myDetails = { ...(data.myDetails || {}), ...(overrides.fields || {}) };
        } else {
          // Server caught up; clear overrides
          localStorage.removeItem(`studentProfileOverrides:${uid}`);
        }
      }
    } catch (e) {
      // ignore localStorage/parse errors
    }
    return data;
  } else {
    console.warn('Student validation failed:', response.data.message);
    return null;
  }
};

export const teacherLogin = async (uid) => {
  try {
    // SIMPLE CACHE: Return cached result if available
    if (teacherLoginCache.has(uid)) {
      return teacherLoginCache.get(uid);
    }

    // CHECK 2: If request already in progress, return the existing promise
    if (pendingTeacherRequests.has(uid)) {
      return pendingTeacherRequests.get(uid);
    }

    // CHECK 3: Create new request and store it
    const requestPromise = performTeacherLogin(uid);
    pendingTeacherRequests.set(uid, requestPromise);

    try {
      const result = await requestPromise;

      // Cache successful result
      if (result) {
        teacherLoginCache.set(uid, result);
      }

      // Clean up pending request
      pendingTeacherRequests.delete(uid);
      return result;
    } catch (error) {
      // Clean up pending request on error
      pendingTeacherRequests.delete(uid);
      throw error;
    }
  } catch (error) {
    console.error('Error during teacher login:', error);
    throw error;
  }
};

const performTeacherLogin = async (uid) => {
  const validateTeacherLogin = httpsCallable(
    CloudFunctions,
    'validateTeacherLogin'
  );
  const response = await validateTeacherLogin({ uid });
  if (response.data.status) {
    return response.data.data;
  } else {
    console.warn('Teacher validation failed:', response.data.message);
    return null;
  }
};

export const schoolAdminLogin = async (uid) => {
  try {
    const validateSchoolAdminLogin = httpsCallable(
      CloudFunctions,
      'validateSchoolAdminLogin'
    );
    const response = await validateSchoolAdminLogin({ uid });
    if (response.data.status) {
      return response.data.data;
    } else {
      console.warn(
        'School ddmin login validation failed:',
        response.data.message
      );
      return null;
    }
  } catch (error) {
    console.error('Error during school admin login login:', error);
    throw error;
  }
};

export const organizationAdminLogin = async (uid) => {
  try {
    const validateOrganisationAdminLogin = httpsCallable(
      CloudFunctions,
      'validateOrganisationAdminLogin'
    );
    const response = await validateOrganisationAdminLogin({ uid });
    if (response.data.status) {
      return response.data.data;
    } else {
      console.warn(
        'School ddmin login validation failed:',
        response.data.message
      );
      return null;
    }
  } catch (error) {
    console.error('Error during school admin login login:', error);
    throw error;
  }
};

export const deleteUserAccountInfo = async (uid, typeOfUser) => {
  try {
    const deleteUserAccount = httpsCallable(
      CloudFunctions,
      'deleteUserAccount'
    );

    const response = await deleteUserAccount({
      uid,
      typeOfUser,
    });
    if (response.data.status) {
      return response.data.message;
    } else {
      return null;
    }
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const fetchUserEmails = async (uids) => {
  try {
    const getUserEmails = httpsCallable(CloudFunctions, 'getUserEmails');
    const response = await getUserEmails({ uids });
    if (response.data) {
      return response.data.emails;
    } else {
      return null;
    }
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const sendEmailForRegisteredUsers = async (data) => {
  try {
    const sendRegistrationEmailsCallable = httpsCallable(
      CloudFunctions,
      'sendRegistrationEmails'
    );

    const response = await sendRegistrationEmailsCallable(data);
    if (response.data.status) {
      return response.data;
    } else {
      return null;
    }
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const registerParentCaregiver = async (data) => {
  try {
    const registerParentCaregiverCallable = httpsCallable(
      CloudFunctions,
      'registerParentCaregiver'
    );

    const response = await registerParentCaregiverCallable(data);
    if (response.data.status) {
      return response.data;
    } else {
      return response.data; // Return error message
    }
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const registerSelfAdvocate = async (data) => {
  try {
    const registerSelfAdvocateCallable = httpsCallable(
      CloudFunctions,
      'registerSelfAdvocate'
    );

    const response = await registerSelfAdvocateCallable(data);
    if (response.data.status) {
      return response.data;
    } else {
      return response.data; 
    }
  } catch (e) {
    console.log(e);
    throw e;
  }
};